import {
  useBookMark,
  useGetCombinedDepartment,
  useGetFavoriteFilter,
  useSearchApproval,
} from '@/hooks/apiHooks';
import { approvalFilter, INIT_FILTER, MyInfoState } from '@/recoil/atom';
import { MAX_APPROVAL_LIST_SIZE } from '..';
import { localFilterToParam } from '../RightContainer';
import { INIT_WEB_APPROVAL_FILTER_STATE } from '@/recoil/init_data';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { Approval, ApprovalFilterType } from '@/types/api';
import { t } from 'i18next';
import { useRouter } from 'next/router';
import { useSnackbar } from '@/hooks/global-tools';
import useFavoriteApproval from '@/components/Approval/@hooks/useFavoriteApproval';
import { isDev, isLocal } from '@/constant';
import { buildApprovalSearchParams } from '@/utils/approval/buildApprovalSearchParams';
import { AxiosError } from 'axios';

const useApproval = () => {
  const myInfo = useAtomValue(MyInfoState);
  const [filters, setFilters] = useAtom(approvalFilter);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { showSuccess, showError } = useSnackbar();
  const params = `${myInfo.departmentId}`;
  const { data } = useGetCombinedDepartment(params);
  const router = useRouter();
  const [favoriteFilterApplied, setFavoriteFilterApplied] = useState<
    string | null
  >(null);

  const favoriteId = useMemo(
    () => router.query.favoriteId as string,
    [router.query],
  );

  const { data: favoriteLinkData } = useGetFavoriteFilter(favoriteId as string);
  const { data: favoriteListData } = useFavoriteApproval();

  // 임시저장은 authoredAt 이 대부분 null 이라 updatedAt 정렬로 강제 (다른 탭은 기존 정렬 유지)
  const isTemporaryContext =
    filters.statuses?.length === 1 && filters.statuses[0]?.value === 'S';
  const listSortType = isTemporaryContext ? 'updatedAt' : filters.sortType;

  const {
    data: approvalData,
    error: approvalError,
    mutate: approvalMutate,
  } = useSearchApproval(
    {
      listSize: MAX_APPROVAL_LIST_SIZE,
      currentPage,
      ...localFilterToParam(filters),
      sortType: listSortType,
      // 통합 → query(고급 문법), 제목/내용 칩 → titleKeyword/contentKeyword 로 분리 전달 (AND 조합)
      ...buildApprovalSearchParams(filters),
    },
    {
      // 검색어 문법 오류(400)는 재시도해도 동일하므로 재시도하지 않는다(BE 부하/스낵바 반복 방지).
      onErrorRetry: (err, _key, _config, revalidate, { retryCount }) => {
        if ((err as AxiosError)?.response?.status === 400) return;
        if (retryCount >= 3) return;
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
    },
  );

  // 고급 검색 문법 오류(400) 안내
  useEffect(() => {
    if ((approvalError as AxiosError)?.response?.status === 400) {
      showError({
        message: t(
          '검색어 문법이 올바르지 않아요. 큰따옴표(")나 괄호의 짝을 확인해 주세요.',
        ),
      });
    }
  }, [approvalError]);

  const {
    data: bookMarkData,
    isLoading: isBookMarkLoading,
    mutate: bookMarkMutate,
  } = useSearchApproval({
    isBookmark: true,
  });

  const {
    data: doApprovalData,
    isLoading: isDoSearchApprovalLoading,
    mutate: doApprovalMutate,
  } = useSearchApproval({
    listSize: MAX_APPROVAL_LIST_SIZE,
    sortType: 'authoredAt',
    orderType: 'desc',
    ...localFilterToParam({
      ...INIT_WEB_APPROVAL_FILTER_STATE,
      currentApproverPassportIds: [{ ...myInfo }],
    }),
  });

  const { data: ingData, isLoading: isIngSearchApprovalLoading } =
    useSearchApproval({
      listSize: MAX_APPROVAL_LIST_SIZE,
      sortType: filters.sortType,
      ...localFilterToParam({
        ...INIT_WEB_APPROVAL_FILTER_STATE,
        authorPassportIds: [{ ...myInfo }],
        statuses: [
          {
            name: '진행중',
            type: 0,
            value: 'P',
          },
        ],
      }),
    });

  const { data: temporaryApproval, isLoading: temporaryApprovalLoading } =
    useSearchApproval({
      listSize: MAX_APPROVAL_LIST_SIZE,
      ...localFilterToParam({
        ...INIT_WEB_APPROVAL_FILTER_STATE,
        authorPassportIds: [{ ...myInfo }],
        statuses: [
          {
            name: '임시저장',
            type: 4,
            value: 'S',
          },
        ],
      }),
      // 임시저장은 authoredAt 이 대부분 null 이라 updatedAt 정렬로 강제
      sortType: 'updatedAt',
      orderType: 'desc',
    });

  const {
    data: unreadReffererData,
    isLoading: isRefererSearchApprovalLoading,
    mutate: myRefererMutate,
  } = useSearchApproval({
    referrerPassportIds: [myInfo.passportId],
    unreadPassportIds: [myInfo.passportId],
    statuses: ['P', 'C', 'H'],
  });

  const handleBookMark = async (documentId: string) => {
    useBookMark(documentId)
      .then(() => {
        let bookMarked = false;
        approvalMutate((currentData: any) => {
          if (!currentData) return currentData; // 기존 데이터가 없으면 그대로 반환
          return {
            ...currentData,
            data: currentData.data.map((item: Approval) => {
              if (item.docId === documentId) {
                bookMarked = item.isBookmark;
                !bookMarked &&
                  showSuccess({ message: t('즐겨찾기에 추가되었습니다.') });
                return { ...item, isBookmark: !item.isBookmark };
              } else {
                return item;
              }
            }),
          };
        }, false);
        bookMarkMutate((currentData: any) => {
          if (!currentData) return currentData;
          return {
            ...currentData,
            meta: {
              ...currentData.meta,
              totalCount: bookMarked
                ? currentData.meta.totalCount - 1
                : currentData.meta.totalCount + 1,
            },
          };
        }, false);
      })
      .catch(() => {
        showError({ message: t('즐겨찾기 처리에 실패했습니다.') });
      });
  };

  const dataItemChange = (id: string, changes: Record<string, any>) => {
    //H 보류, C 승인, R 거절, P 상태 전
    approvalMutate((currentData: any) => {
      if (!currentData) return currentData;
      const newData = currentData?.data.filter(
        (item: any) => item.docId !== id,
      );
      return {
        data:
          changes['status'] === 'C' || changes['status'] === 'R'
            ? newData
            : currentData?.data.map((item: any) =>
                item.docId == id ? { ...item, ...changes } : item,
              ),
        meta: {
          ...currentData?.meta,
          totalCount:
            changes['status'] === 'C' || changes['status'] === 'R'
              ? newData.length
              : currentData?.meta.totalCount,
        },
      };
    }, false);
    doApprovalMutate((currentData: any) => {
      if (!currentData) return currentData;
      const newData = currentData?.data.filter(
        (item: any) => item.docId !== id,
      );
      return {
        data:
          changes['status'] === 'C' || changes['status'] === 'R'
            ? newData
            : currentData?.data.map((item: any) =>
                item.docId == id ? { ...item, ...changes } : item,
              ),
        meta: {
          ...currentData?.meta,
          totalCount:
            changes['status'] === 'C' || changes['status'] === 'R'
              ? newData.length
              : currentData?.meta.totalCount,
        },
      };
    }, false);
  };
  const setFilter = (id: ApprovalFilterType) => {
    // 사용자가 수동으로 필터 선택 중임을 표시 (useEffect가 덮어쓰지 않도록)
    setFavoriteFilterApplied('__USER_MANUAL_SELECTION__');

    if (favoriteId) {
      const { favoriteId: _, ...queryWithoutFavoriteId } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: queryWithoutFavoriteId,
        },
        undefined,
        { shallow: true },
      );
    }

    switch (id) {
      case ApprovalFilterType['즐겨찾기']: //BookMark
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          endAuthoredAt: [],
          isBookmark: true,
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['전체 문서']: //전체 문서
        setFilters({
          ...INIT_FILTER,
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['내가 결재자인']: //내가 결재자인
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          approverPassportIds: [{ ...myInfo }],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['결재할 문서']: //결재할 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          currentApproverPassportIds: [{ ...myInfo }],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['결재한 문서']: //결재한 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          approverPassportIds: [{ ...myInfo }],
          approverStatuses: ['C', 'H', 'R'],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['협조결재한 문서']: //협조결재한 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          coApproverPassportIds: [{ ...myInfo }],
          coApproverStatuses: ['C', 'H', 'R'],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['내가 작성자인']: //내가 작성자인
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          authorPassportIds: [{ ...myInfo }],
          id: id,
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['진행중 문서']: //진행중 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          authorPassportIds: [{ ...myInfo }],
          statuses: [
            {
              name: t('진행중'),
              type: 0,
              value: 'P',
            },
          ],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['임시저장 문서']: //임시저장 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          authorPassportIds: [{ ...myInfo }],
          statuses: [{ name: t('임시저장'), type: 4, value: 'S' }],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['완료 문서']: //완료 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          authorPassportIds: [{ ...myInfo }],
          statuses: [
            {
              name: t('완료'),
              type: 1,
              value: 'C',
            },
          ],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['반려 문서']: //반려 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          authorPassportIds: [{ ...myInfo }],
          statuses: [
            {
              name: t('반려'),
              type: 2,
              value: 'R',
            },
          ],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;

      case ApprovalFilterType['내가 참조자인 문서']: //내가 참조자인 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          referrerPassportIds: [{ ...myInfo }],
          statuses: [
            {
              name: t('진행중'),
              type: 0,
              value: 'P',
            },
            {
              name: t('보류'),
              type: 3,
              value: 'H',
            },

            {
              name: t('완료'),
              type: 1,
              value: 'C',
            },
          ],
          unreadPassportIds:
            (unreadReffererData?.meta.totalCount || 0) > 0
              ? [{ ...myInfo }]
              : [],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
      case ApprovalFilterType['팀원의 작성 문서']: //팀원의 작성 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          authorDepartmentIds: data || [],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;

      case ApprovalFilterType['팀원의 협조결재 문서']: //팀원의 협조결재 문서
        setFilters({
          ...INIT_WEB_APPROVAL_FILTER_STATE,
          id: id,
          approverDepartmentIds: data || [],
          keyword: filters.keyword,
          searchFields: filters.searchFields,
        });
        break;
    }
  };

  useEffect(() => {
    // Dev 환경이 아니면 favoriteId 기능 비활성화
    if (!isDev && !isLocal) return;

    if (!favoriteId) {
      // URL에 favoriteId가 없으면 초기화
      if (favoriteFilterApplied !== '__USER_MANUAL_SELECTION__') {
        setFavoriteFilterApplied(null);
      }
      return;
    }
    if (!favoriteListData) return;

    // 이미 이 favoriteId로 필터를 적용했으면 스킵
    if (favoriteFilterApplied === favoriteId) return;

    // 사용자가 수동으로 필터를 선택 중이면 스킵
    if (favoriteFilterApplied === '__USER_MANUAL_SELECTION__') return;

    const approvalFavoriteLinkData = favoriteListData.data.find(
      (item) => item.id === favoriteId,
    );

    // 내 필터 목록에 있는 경우
    if (approvalFavoriteLinkData?.params) {
      setFilters({
        ...INIT_WEB_APPROVAL_FILTER_STATE,
        ...approvalFavoriteLinkData.params,
        keyword: approvalFavoriteLinkData.params.keyword || '',
        id: approvalFavoriteLinkData.id,
        filterName: approvalFavoriteLinkData.name,
      });
      setFavoriteFilterApplied(favoriteId);
      return;
    }

    // 공유받은 필터인 경우
    if (!favoriteLinkData?.params) return;

    setFilters({
      ...INIT_WEB_APPROVAL_FILTER_STATE,
      ...favoriteLinkData.params,
      keyword: favoriteLinkData.params.keyword || '',
      filterName: favoriteLinkData.name,
      id: ApprovalFilterType['전체 문서'],
    });
    setFavoriteFilterApplied(favoriteId);
  }, [
    favoriteListData,
    favoriteId,
    favoriteLinkData,
    favoriteFilterApplied,
    setFilters,
  ]);

  return {
    approvalData,
    approvalMutate,
    bookMarkData,
    isBookMarkLoading,
    bookMarkMutate,
    doApprovalData,
    isDoSearchApprovalLoading,
    doApprovalMutate,
    ingData,
    isIngSearchApprovalLoading,
    unreadReffererData,
    isRefererSearchApprovalLoading,
    myRefererMutate,
    temporaryApproval,
    temporaryApprovalLoading,
    setCurrentPage,
    currentPage,
    handleBookMark,
    dataItemChange,
    setFilter,
    favoriteLinkData,
    favoriteId,
  };
};
export default useApproval;
